import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/lib/toast';
import { api } from '@/lib/api';
import { errToToast } from '@/lib/humanizeApiError';
import i18n from '@/i18n';

/**
 * Blocked-users management for the Privacy settings tab.
 *
 * The block/unblock endpoints (`/api/me/blocks`) already exist server-side
 * and are exercised by the message context menu (`api.blockUser`). This hook
 * adds the missing read + remove surface: list who you've blocked and undo it.
 *
 * Server contract (server/src/routes/blocks.ts):
 *   GET    /api/me/blocks        → { blocks: { user, createdAt }[] }
 *   DELETE /api/me/blocks/:id    → 204
 */

export interface BlockedUser {
  id: string;
  platformId: string;
  username: string;
  displayName: string | null;
  avatarUrl: string | null;
}

export interface BlockedEntry {
  user: BlockedUser;
  createdAt: string;
}

/** Shared so other call-sites (e.g. blocking from a message) can invalidate it. */
export const BLOCKS_QUERY_KEY = ['me', 'blocks'] as const;

export interface UseBlockedUsers {
  blocks: BlockedEntry[];
  /** Ids the current user has blocked — for quick "is X blocked?" checks. */
  blockedIds: Set<string>;
  isBlocked: (userId: string) => boolean;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
  /** Block a user; toasts on failure, refetches on success. */
  block: (userId: string) => void;
  /** Unblock a user; toasts on failure, refetches the list on success. */
  unblock: (userId: string) => void;
  /** Block if not blocked, unblock if blocked. */
  toggle: (userId: string) => void;
  /** The id currently being unblocked (for per-row spinners), else null. */
  unblockingId: string | null;
  /** The id currently being block-or-unblock mutated, else null. */
  pendingId: string | null;
}

export function useBlockedUsers(): UseBlockedUsers {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: BLOCKS_QUERY_KEY,
    queryFn: () => api.listBlocks(),
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: BLOCKS_QUERY_KEY });
  };

  const blockMutation = useMutation({
    mutationFn: (userId: string) => api.blockUser(userId),
    onSuccess: invalidate,
    onError: (err) => {
      toast.error(...errToToast(err, i18n.t('prof.blocked.blockFailed')));
    },
  });

  const unblockMutation = useMutation({
    mutationFn: (userId: string) => api.unblockUser(userId),
    onSuccess: invalidate,
    onError: (err) => {
      toast.error(...errToToast(err, i18n.t('prof.blocked.unblockFailed')));
    },
  });

  const blocks = query.data?.blocks ?? [];
  const blockedIds = new Set(blocks.map((b) => b.user.id));
  const isBlocked = (userId: string) => blockedIds.has(userId);

  const unblockingId = unblockMutation.isPending ? (unblockMutation.variables ?? null) : null;
  const blockingId = blockMutation.isPending ? (blockMutation.variables ?? null) : null;

  return {
    blocks,
    blockedIds,
    isBlocked,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => {
      void query.refetch();
    },
    block: (userId: string) => blockMutation.mutate(userId),
    unblock: (userId: string) => unblockMutation.mutate(userId),
    toggle: (userId: string) =>
      isBlocked(userId) ? unblockMutation.mutate(userId) : blockMutation.mutate(userId),
    unblockingId,
    pendingId: unblockingId ?? blockingId,
  };
}
